import React, { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  TimePicker,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {
  ADD_DSP_BILLING_CADENCE_WHITELIST,
  LIST_DSP_BILLING_CADENCE_WHITELIST,
  REMOVE_DSP_BILLING_CADENCE_WHITELIST,
} from './graphql/dspBillingCadenceGraphql';

type WhitelistRow = {
  id: string;
  dspId: string;
  status: string;
  effectiveFromLA: string;
  billingSummaryTimeLA: string;
  removedAtLA: string | null;
  createdBy: string | null;
  removedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

type AddFormValues = {
  dspId: number;
  effectiveFromLA: Dayjs;
  billingSummaryTimeLA?: Dayjs;
};

const DATETIME_FMT = 'YYYY-MM-DD HH:mm:ss';
const TIME_FMT = 'HH:mm:ss';

const DspBillingCadenceWhitelistPage: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);

  const [filterDspId, setFilterDspId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm<AddFormValues>();

  const { data, loading, refetch } = useQuery(LIST_DSP_BILLING_CADENCE_WHITELIST, {
    variables: {
      dspId: filterDspId,
      status: filterStatus,
      pageRequest: { page, size: pageSize },
    },
    fetchPolicy: 'network-only',
  });

  const [addWhitelist, { loading: adding }] = useMutation(ADD_DSP_BILLING_CADENCE_WHITELIST);
  const [removeWhitelist, { loading: removing }] = useMutation(REMOVE_DSP_BILLING_CADENCE_WHITELIST);

  const rows: WhitelistRow[] = data?.listDspBillingCadenceWhitelist?.content ?? [];
  const total: number = data?.listDspBillingCadenceWhitelist?.totalElements ?? 0;

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      const result = await addWhitelist({
        variables: {
          input: {
            dspId: values.dspId,
            effectiveFromLA: values.effectiveFromLA.format(DATETIME_FMT),
            billingSummaryTimeLA: values.billingSummaryTimeLA
              ? values.billingSummaryTimeLA.format(TIME_FMT)
              : null,
          },
        },
      });
      if (result.data?.addDspBillingCadenceWhitelist) {
        message.success(`已加入白名单，记录ID=${result.data.addDspBillingCadenceWhitelist}`);
        setAddModalOpen(false);
        addForm.resetFields();
        refetch();
      }
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const handleRemove = async (dspId: string) => {
    try {
      const result = await removeWhitelist({ variables: { dspId: Number(dspId) } });
      if (result.data?.removeDspBillingCadenceWhitelist) {
        message.success('已移除白名单');
        refetch();
      } else {
        message.warning('未命中有效的白名单记录');
      }
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const columns: ColumnsType<WhitelistRow> = [
    { title: '记录ID', dataIndex: 'id', width: 100 },
    { title: 'DSP ID', dataIndex: 'dspId', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (s: string) =>
        s === 'ACTIVE' ? <Tag color="green">ACTIVE</Tag> : <Tag>REMOVED</Tag>,
    },
    {
      title: '生效时间（LA）',
      dataIndex: 'effectiveFromLA',
      width: 180,
    },
    {
      title: '账期汇总时间（LA）',
      dataIndex: 'billingSummaryTimeLA',
      width: 150,
    },
    {
      title: '移除时间（LA）',
      dataIndex: 'removedAtLA',
      width: 180,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      width: 160,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '移除人',
      dataIndex: 'removedBy',
      width: 160,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '更新时间（UTC）',
      dataIndex: 'updatedAt',
      width: 200,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) =>
        canOperate && record.status === 'ACTIVE' ? (
          <Popconfirm
            title={`确认移除 DSP ${record.dspId} 的白名单？`}
            onConfirm={() => handleRemove(record.dspId)}
            disabled={removing}
          >
            <Button type="link" size="small" danger>
              移除
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Card title="DSP 账单节奏白名单" bordered={false}>
      <Space wrap className="mb-4">
        <InputNumber
          placeholder="DSP ID 过滤"
          value={filterDspId ?? undefined}
          onChange={v => setFilterDspId(v == null ? null : Number(v))}
          style={{ width: 180 }}
        />
        <Input
          placeholder="状态过滤：ACTIVE / REMOVED"
          value={filterStatus ?? ''}
          onChange={e => setFilterStatus(e.target.value.trim() || null)}
          style={{ width: 220 }}
          allowClear
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            setPage(1);
            refetch();
          }}
        >
          查询
        </Button>
        {canOperate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            加入白名单
          </Button>
        )}
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1400 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
      />

      <Modal
        title="加入 DSP 白名单（快节奏账单）"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAdd}
        confirmLoading={adding}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="dspId"
            label="DSP ID"
            rules={[{ required: true, message: '请输入 DSP ID' }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="结算客户 ID" />
          </Form.Item>
          <Form.Item
            name="effectiveFromLA"
            label="生效时间（美西本地）"
            rules={[{ required: true, message: '请选择生效时间' }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format={DATETIME_FMT}
            />
          </Form.Item>
          <Form.Item
            name="billingSummaryTimeLA"
            label="账期汇总时间（美西本地，默认 08:00:00）"
            tooltip="不填则默认 08:00:00（LA）"
          >
            <TimePicker style={{ width: '100%' }} format={TIME_FMT} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DspBillingCadenceWhitelistPage;
